import dayjs from 'dayjs';
import userData from 'tradera-apps/syi/script/app_react/services/user-data';
import draftService from 'tradera-apps/syi/script/app_react/services/draft-service';
import { getCurrentLanguage } from 'tradera-lang/boot-languages';

dayjs.locale(getCurrentLanguage());

var defaultDraft;
var initialState;

const initModuleGlobals = () => {
    if (initialState) {
        return initialState;
    }

    defaultDraft = userData.defaultDrafts.newItem;

    initialState = {
        daysOptions: makeDaysOptions(3, 14),
        hourOptions: makeHourOptions(),
        minuteOptions: makeMinuteOptions(),
        isCustomEndTimeVisible: false,
        customEndTimePrice: 10,
        isRestartAvailable: true,
        durationDays: defaultDraft.durationDays,
        customEndTimeHour: null,
        customEndTimeMinute: null,
        summaryText: '',
        allowZeroDayAuctions: false,
        validationErrors: {}
    };
};

function makeDaysOptions(min, max) {
    var result = [];
    var minDays = min;
    var maxDays = max;
    for (var numberOfDays = minDays; numberOfDays <= maxDays; numberOfDays++) {
        result.push({
            days: numberOfDays,
            selected: false
        });
    }
    return result;
}

function makeHourOptions() {
    var result = [];
    for (var i = 0; i < 24; i++) {
        var hour = i < 10 ? '0' + i : parseInt(i, 10);
        result.push({ value: hour, text: hour });
    }
    return result;
}

function makeMinuteOptions() {
    var result = [];
    for (var i = 0; i < 60; i++) {
        var minute = i < 10 ? '0' + i : parseInt(i, 10);
        result.push({ value: minute, text: minute });
    }
    return result;
}

const getCustomEndTime = state => {
    if (!state.customEndTimeHour || !state.customEndTimeMinute) {
        return null;
    }

    return state.customEndTimeHour + ':' + state.customEndTimeMinute;
};

function resolveSummaryText(days, customEndTime, isPureBin, isChangeItem) {
    const durationDays = isPureBin ? 30 : days;
    const localizations = [['syi_summary_duration_days', { durationDays }]];
    if (!isChangeItem) {
        localizations.push([
            'syi_summary_duration_endDate',
            {
                endDate: dayjs()
                    .add(durationDays, 'days')
                    .format('ddd D MMM')
            }
        ]);
    }
    if (customEndTime) {
        localizations.push([
            isChangeItem
                ? 'syi_summary_duration_changed_customEndTime'
                : 'syi_summary_duration_default_customEndTime',
            { customEndTime }
        ]);
    }
    if (!isChangeItem) {
        localizations.push(['syi_summary_duration_closingParenthesis']);
    }
    return localizations;
}

const getValidationErrors = (
    durationDays,
    customEndTime,
    allowZeroDayAuctions
) => {
    let customEndTimeError = null;
    let customHour = dayjs().hour();
    let customMinute = dayjs().minute();

    if (customEndTime) {
        const splitTime = customEndTime.split(':', 2);
        customHour = splitTime[0];
        customMinute = splitTime[1];
    }

    if (allowZeroDayAuctions) {
        let endDate = dayjs()
            .startOf('date')
            .hour(customHour)
            .minute(customMinute)
            .add(durationDays, 'day');
        const earliestEndDate = dayjs().add(5, 'minute');
        if (endDate.isBefore(earliestEndDate)) {
            customEndTimeError = 'syi_validation_duration_too_short_zero_day'; //'Din valda sluttid är för tidig. Annonser får avslutas tidigast efter 5 minuter.';
        }
    } else if (customEndTime) {
        let endDate = dayjs()
            .startOf('date')
            .hour(customHour)
            .minute(customMinute)
            .add(durationDays, 'day');
        const earliestEndDate = dayjs().add(3, 'day');

        if (endDate.isBefore(earliestEndDate)) {
            customEndTimeError = 'syi_validation_duration_too_short'; //'Din valda sluttid är för tidig. Annonser får avslutas tidigast efter 72 timmar.';
        }
    }

    return customEndTimeError ? { customEndTime: customEndTimeError } : {};
};

const newStateWithoutCampaignRelatedProps = (
    state,
    durationDays,
    customEndTime,
    isPureBin,
    allowZeroDayAuctions,
    isChangeItem
) => {
    const summaryText = resolveSummaryText(
        durationDays,
        customEndTime,
        isPureBin,
        isChangeItem
    );
    const validationErrors = getValidationErrors(
        durationDays,
        customEndTime,
        allowZeroDayAuctions
    );
    return {
        ...state,
        durationDays,
        customEndTime,
        summaryText,
        isCustomEndTimeVisible: !!customEndTime,
        validationErrors
    };
};

export default (state, action) => {
    initModuleGlobals();
    state = state || initialState;
    switch (action.type) {
        case 'SET_DURATION_DAYS': {
            const durationDays = action.payload;
            const summaryText = resolveSummaryText(
                durationDays,
                getCustomEndTime(state),
                action.isPureBin,
                action.isChangeItem
            );
            const validationErrors = getValidationErrors(
                durationDays,
                getCustomEndTime(state),
                state.allowZeroDayAuctions
            );
            return {
                ...state,
                durationDays,
                summaryText,
                validationErrors
            };
        }
        case 'SET_CUSTOM_END_TIME': {
            const isPureBin = action.isPureBin;
            const summaryText = resolveSummaryText(
                state.durationDays,
                action.payload,
                isPureBin,
                action.isChangeItem
            );
            const validationErrors = getValidationErrors(
                state.durationDays,
                action.payload,
                state.allowZeroDayAuctions
            );
            let customEndTimeHour = null;
            let customEndTimeMinute = null;
            if (typeof action.payload === 'string') {
                let customEndTime = action.payload.split(':');
                customEndTimeHour = customEndTime[0];
                customEndTimeMinute = customEndTime[1];
            }
            return {
                ...state,
                customEndTimeHour: customEndTimeHour,
                customEndTimeMinute: customEndTimeMinute,
                isCustomEndTimeVisible: !!action.payload,
                summaryText,
                validationErrors
            };
        }
        case 'RESET_DURATION_AND_END_TIME': {
            const durationDays = draftService.getDefaultTemplateValue(
                'durationDays'
            );
            const customEndTime = draftService.getDefaultTemplateValue(
                'customEndTime'
            );
            let newState = newStateWithoutCampaignRelatedProps(
                state,
                durationDays,
                customEndTime,
                action.isPureBin,
                state.allowZeroDayAuctions
            );
            newState.customEndTimePrice = initialState.customEndTimePrice;
            return newState;
        }
        case 'RESET_CUSTOM_END_TIME': {
            return {
                ...state,
                customEndTimeHour: initialState.customEndTimeHour,
                customEndTimeMinute: initialState.customEndTimeMinute
            };
        }
        case 'SET_CUSTOM_END_TIME_FEE_FREE_FOR_CAMPAIGN':
            return { ...state, customEndTimePrice: 0 };
        case 'RESET_CUSTOM_END_TIME_FEE_FREE_FOR_CAMPAIGN':
            return {
                ...state,
                customEndTimePrice: initialState.customEndTimePrice
            };
        case 'SET_AUCTION_BIN_PRICE': {
            const validationErrors = getValidationErrors(
                state.durationDays,
                getCustomEndTime(state),
                state.allowZeroDayAuctions
            );
            return { ...state, validationErrors };
        }
        case 'SET_IS_PURE_BIN_SELECTED': {
            const durationDays = state.durationDays;
            const customEndTime = getCustomEndTime(state);
            const summaryText = resolveSummaryText(
                durationDays,
                customEndTime,
                action.isPureBinSelected,
                action.isChangeItem
            );
            return { ...state, summaryText };
        }
        case 'DISABLE_ZERO_DAY_AUCTIONS': {
            const durationDays =
                state.durationDays >= 3 ? state.durationDays : 3;
            return {
                ...state,
                allowZeroDayAuctions: false,
                daysOptions: makeDaysOptions(3, 14),
                durationDays
            };
        }
        case 'ENABLE_ZERO_DAY_AUCTIONS': {
            const daysOptions = makeDaysOptions(0, 14);
            return { ...state, daysOptions, allowZeroDayAuctions: true };
        }
        case 'ENABLE_RESTART':
            return { ...state, isRestartAvailable: true };
        case 'DISABLE_RESTART':
            return { ...state, isRestartAvailable: false };
        default:
            return state;
    }
};
